import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { ReactNode } from 'react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/money';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { PageHead } from '@/components/layout/PageHead';
import { StatBand } from '@/components/ui/StatBand';
import { LogoLoading } from '@/components/ui/LogoLoading';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import { Input } from '@/components/shadcn/input';
import { cn } from '@/lib/utils';
import type { DashboardSummary, TopExpenseCategory } from '@/types';

// ─── Types ────────────────────────────────────────────────────────────────────

type ChartPeriod = 'today' | 'week' | 'month' | 'year' | 'custom';

interface ChartPoint { label: string; revenue: string; expenses: string; profit: string; }

interface PeriodProps {
  period: ChartPeriod;
  customFrom: string;
  customTo: string;
}

interface OverdueInvoiceRow {
  id: string;
  number: string;
  customer_name: string | null;
  days_overdue: number;
  balance_due: string;
}

/** A decimal string from the API, or undefined when it is absent. */
const num = (v: string | undefined): number | undefined =>
  v === undefined || v === null ? undefined : Number(v);

// ─── Palette ──────────────────────────────────────────────────────────────────
//
// Every chart colour is a theme token, so the same series reads the same on the
// light and the dark palette — and follows a tenant's accent where it uses one.

const C = {
  revenue: 'var(--chart-3)',
  expenses: 'var(--destructive)',
  profit: 'var(--chart-4)',
  cash: 'var(--chart-2)',
  current: 'var(--chart-2)',
  overdue: 'var(--chart-4)',
  grid: 'var(--border)',
  axis: 'var(--muted-foreground)',
};

const DONUT_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--destructive)',
  'var(--muted-foreground)',
];

const TOOLTIP_STYLE = {
  contentStyle: {
    background: 'var(--popover)',
    border: '1px solid var(--edge)',
    borderRadius: 'calc(var(--radius) + 2px)',
    fontSize: 12,
    padding: '8px 10px',
    color: 'var(--popover-foreground)',
    boxShadow: 'var(--elev-3)',
  },
  labelStyle: {
    color: 'var(--muted-foreground)',
    marginBottom: 6,
    fontSize: 11,
    letterSpacing: '0.055em',
    textTransform: 'uppercase' as const,
  },
  itemStyle: { color: 'var(--popover-foreground)', padding: '1px 0' },
};

const AXIS_TICK = { fill: C.axis, fontSize: 11, letterSpacing: 0.2 };

/**
 * Chart chrome should be furniture, not pattern. A dashed grid in both axes
 * puts a visible mesh behind every series; one soft horizontal rule per tick
 * is all a reader actually uses to compare heights.
 */
const GRID_PROPS = {
  stroke: C.grid,
  strokeOpacity: 0.7,
  vertical: false,
} as const;

const Y_FMT = (v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatChartLabel(label: string, period: ChartPeriod): string {
  if (period === 'today') return label;
  if (period === 'week') {
    const d = new Date(label + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
  if (period === 'month') {
    const d = new Date(label + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  const [y, m] = label.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function formatRelative(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(lang === 'ar' ? 'ar' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === 'ar' ? 'ar' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function buildQueryParams(p: PeriodProps): string {
  if (p.period === 'custom' && p.customFrom && p.customTo)
    return `period=custom&from=${p.customFrom}&to=${p.customTo}`;
  return `period=${p.period}`;
}

// ─── Chart frame ──────────────────────────────────────────────────────────────

/**
 * Charts are laid out left-to-right in both languages: a time axis that runs
 * backwards is harder to read than a mirrored one, and recharts has no RTL mode.
 */
function ChartFrame({ height, children }: { height: number; children: ReactNode }) {
  return (
    <div dir="ltr" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children as never}
      </ResponsiveContainer>
    </div>
  );
}

function ChartState({ height, children }: { height: number; children: ReactNode }) {
  return (
    <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
      {children}
    </div>
  );
}

/** Legend swatch + label + total, so the number is next to the colour it names. */
function Series({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <span className="flex items-center gap-2 text-xs">
      <span className="size-2.5 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
      <span className="text-muted-foreground">{label}</span>
      <strong className="font-medium tabular-nums">{value}</strong>
    </span>
  );
}

// ─── Shared Period Bar ────────────────────────────────────────────────────────

interface PeriodBarProps {
  period: ChartPeriod;
  setPeriod: (p: ChartPeriod) => void;
  customFrom: string;
  customTo: string;
  setCustomFrom: (v: string) => void;
  setCustomTo: (v: string) => void;
}

function PeriodBar({ period, setPeriod, customFrom, customTo, setCustomFrom, setCustomTo }: PeriodBarProps) {
  const { t } = useI18n();
  const keys: { key: ChartPeriod; label: string }[] = [
    { key: 'today', label: t('today') },
    { key: 'week', label: t('this_week') },
    { key: 'month', label: t('this_month') },
    { key: 'year', label: t('this_year') },
    { key: 'custom', label: t('custom') },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex items-center rounded-lg border border-border/70 bg-muted/50 p-[3px] dark:bg-muted/30">
        {keys.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriod(p.key)}
            aria-pressed={period === p.key}
            className={cn(
              'inline-flex h-7 items-center rounded-md px-3 text-xs font-medium transition-colors',
              period === p.key
                ? 'bg-card text-foreground shadow-[var(--elev-1)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
      {period === 'custom' && (
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="h-8 w-auto text-xs"
          />
          <span className="text-xs text-muted-foreground rtl:rotate-180" aria-hidden>→</span>
          <Input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="h-8 w-auto text-xs"
          />
        </div>
      )}
    </div>
  );
}

// ─── Income & Expense Bar Chart ───────────────────────────────────────────────

function IncomeExpenseChart({ currency, pp }: { currency: string; pp: PeriodProps }) {
  const { t } = useI18n();
  const enabled = pp.period !== 'custom' || (!!pp.customFrom && !!pp.customTo);
  const q = useQuery<{ points: ChartPoint[] }, ApiError>({
    queryKey: ['dashboard', 'chart-bar', pp.period, pp.customFrom, pp.customTo],
    queryFn: () => api<{ points: ChartPoint[] }>(`/dashboard/chart?${buildQueryParams(pp)}`),
    enabled,
  });
  const chartData = (q.data?.points ?? []).map((p) => ({
    name: formatChartLabel(p.label, pp.period),
    revenue: Number(p.revenue),
    expenses: Number(p.expenses),
  }));
  const totalRevenue = chartData.reduce((s, p) => s + p.revenue, 0);
  const totalExpenses = chartData.reduce((s, p) => s + p.expenses, 0);

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle className="text-sm font-medium">{t('income_and_expense')}</CardTitle>
        <CardDescription className="flex flex-wrap gap-x-5 gap-y-1 pt-1">
          <Series color={C.revenue} label={t('revenue')} value={fmt(totalRevenue, currency)} />
          <Series color={C.expenses} label={t('expenses')} value={fmt(totalExpenses, currency)} />
        </CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <ChartState height={220}><LogoLoading size={40} /></ChartState>
        ) : chartData.length === 0 ? (
          <ChartState height={220}>{t('no_data')}</ChartState>
        ) : (
          <ChartFrame height={220}>
            <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barCategoryGap="34%">
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={60} tickFormatter={Y_FMT} />
              <Tooltip {...TOOLTIP_STYLE} cursor={{ fill: 'var(--foreground)', opacity: 0.05 }} formatter={(v, n) => [fmt(Number(v ?? 0), currency), String(n)]} />
              <Bar dataKey="revenue" name={t('revenue')} fill={C.revenue} radius={[5, 5, 0, 0]} maxBarSize={34} />
              <Bar dataKey="expenses" name={t('expenses')} fill={C.expenses} radius={[5, 5, 0, 0]} maxBarSize={34} />
            </BarChart>
          </ChartFrame>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Top Expenses Donut ───────────────────────────────────────────────────────

function TopExpensesChart({ currency, pp }: { currency: string; pp: PeriodProps }) {
  const { t } = useI18n();
  const topPeriod = (pp.period === 'month' || pp.period === 'today' || pp.period === 'week') ? 'month' : 'year';
  const q = useQuery<{ categories: TopExpenseCategory[] }, ApiError>({
    queryKey: ['dashboard', 'top-expenses', topPeriod],
    queryFn: () => api<{ categories: TopExpenseCategory[] }>(`/dashboard/top-expenses?period=${topPeriod}`),
  });
  const data = (q.data?.categories ?? []).map((c, i) => ({
    name: c.category,
    value: Number(c.total),
    color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle className="text-sm font-medium">{t('top_expenses')}</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <ChartState height={220}><LogoLoading size={40} /></ChartState>
        ) : data.length === 0 ? (
          <ChartState height={220}>{t('no_data')}</ChartState>
        ) : (
          <div className="flex flex-wrap items-center gap-5">
            <div className="relative shrink-0">
              <div dir="ltr">
                <ResponsiveContainer width={180} height={180}>
                  <PieChart>
                    <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value" strokeWidth={0}>
                      {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip {...TOOLTIP_STYLE} formatter={(v) => [fmt(Number(v), currency), '']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
                <span className="text-[10px] text-muted-foreground">{t('all_expenses')}</span>
                <span className="text-xs font-semibold tabular-nums">{fmt(total, currency)}</span>
              </div>
            </div>
            <ul className="flex min-w-0 flex-1 flex-col gap-2">
              {data.map((item) => (
                <li key={item.name} className="flex items-center justify-between gap-3 text-xs">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="size-2.5 shrink-0 rounded-full" style={{ background: item.color }} aria-hidden />
                    <span className="truncate">{item.name}</span>
                  </span>
                  <span className="shrink-0 tabular-nums">{fmt(item.value, currency)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Receivables / Payables Cards ─────────────────────────────────────────────

/**
 * What is owed, both ways, in ONE card.
 *
 * Receivables and payables were two identical cards side by side, each with its
 * own title, total, bar and legend — eight elements to say two numbers. They
 * are the two halves of the same question ("what is outstanding?") and are only
 * ever read against each other, so they share a surface and the legend is
 * stated once instead of twice.
 *
 * The bar is the point of the card, not decoration: what matters about 41,000
 * BHD receivable is how much of it is already late, and that is a proportion,
 * which a bar answers instantly and two more numbers do not.
 */
function BalanceBar({ current, overdue, total }: { current: number; overdue: number; total: number }) {
  if (total <= 0) return <div className="mt-3 h-2 rounded-full bg-muted" />;
  return (
    /* `dir="ltr"` deliberately: the bar is a proportion, not text. Mirroring it
       in Arabic would put "overdue" on the side the legend does not. */
    <div dir="ltr" className="mt-3 flex h-2 gap-0.5 overflow-hidden rounded-full bg-muted">
      <div className="rounded-full" style={{ width: `${(current / total) * 100}%`, background: C.current }} />
      <div className="rounded-full" style={{ width: `${(overdue / total) * 100}%`, background: C.overdue }} />
    </div>
  );
}

function OutstandingSide({
  title,
  currency,
  total,
  current,
  overdue,
  href,
}: {
  title: string;
  currency: string;
  total: number;
  current: number;
  overdue: number;
  href: string;
}) {
  const { t } = useI18n();
  const latePct = total > 0 ? (overdue / total) * 100 : 0;

  return (
    <div className="min-w-0 p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="eyebrow">{title}</h3>
        <Link to={href} className="text-[11.5px] text-muted-foreground hover:text-foreground">
          {t('view_all')}
        </Link>
      </div>

      <p className="mt-2.5 truncate text-[1.5rem] leading-none font-semibold tracking-tight tabular-nums">
        {fmt(total, currency)}
      </p>

      <BalanceBar current={current} overdue={overdue} total={total} />

      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1">
        <Series color={C.current} label={t('current')} value={fmt(current, currency)} />
        <Series color={C.overdue} label={t('overdue')} value={fmt(overdue, currency)} />
      </div>

      {/* The sentence the bar is making, said out loud — but only when it is
          worth saying. Under a tenth late is normal and needs no comment. */}
      {latePct >= 10 ? (
        <p className="mt-2.5 text-[11.5px] text-destructive">
          {t('pct_overdue').replace('{n}', latePct.toFixed(0))}
        </p>
      ) : null}
    </div>
  );
}

function ReceivablesPayablesRow({ stats, currency }: { stats: DashboardSummary['stats']; currency: string }) {
  const { t } = useI18n();

  return (
    <section className="grid rounded-xl border bg-card shadow-[var(--elev-1)] sm:grid-cols-2 sm:[&>*+*]:border-s">
      <OutstandingSide
        title={t('total_receivables')}
        currency={currency}
        href="/invoices"
        total={Number(stats.total_receivables ?? 0)}
        current={Number(stats.receivables_current ?? 0)}
        overdue={Number(stats.receivables_overdue ?? 0)}
      />
      <OutstandingSide
        title={t('total_payables')}
        currency={currency}
        href="/bills"
        total={Number(stats.total_payables ?? 0)}
        current={Number(stats.payables_current ?? 0)}
        overdue={Number(stats.payables_overdue ?? 0)}
      />
    </section>
  );
}

// ─── Cash Flow Chart ──────────────────────────────────────────────────────────

function CashFlowChart({ currency, pp }: { currency: string; pp: PeriodProps }) {
  const { t } = useI18n();
  const enabled = pp.period !== 'custom' || (!!pp.customFrom && !!pp.customTo);
  const q = useQuery<{ points: ChartPoint[] }, ApiError>({
    queryKey: ['dashboard', 'cashflow', pp.period, pp.customFrom, pp.customTo],
    queryFn: () => api<{ points: ChartPoint[] }>(`/dashboard/chart?${buildQueryParams(pp)}`),
    enabled,
  });

  let running = 0;
  const chartData = (q.data?.points ?? []).map((p) => {
    running += Number(p.revenue) - Number(p.expenses);
    return { name: formatChartLabel(p.label, pp.period), cash: running, incoming: Number(p.revenue), outgoing: Number(p.expenses) };
  });
  const lastCash = chartData.at(-1)?.cash ?? 0;
  const totalIn = chartData.reduce((s, p) => s + p.incoming, 0);
  const totalOut = chartData.reduce((s, p) => s + p.outgoing, 0);

  const figures: { label: string; value: string; color: string }[] = [
    { label: t('revenue'), value: `${fmt(totalIn, currency)} (+)`, color: C.revenue },
    { label: t('expenses'), value: `${fmt(totalOut, currency)} (−)`, color: C.expenses },
    { label: t('profit'), value: fmt(lastCash, currency), color: lastCash >= 0 ? C.revenue : C.expenses },
  ];

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle className="text-sm font-medium">{t('net_income_trend')}</CardTitle>
        <CardDescription className="flex flex-wrap gap-x-8 gap-y-2 pt-2">
          {figures.map((f) => (
            <span key={f.label} className="grid gap-0.5">
              <span className="text-[11px] text-muted-foreground">{f.label}</span>
              <span className="text-sm font-semibold tabular-nums" style={{ color: f.color }}>
                {f.value}
              </span>
            </span>
          ))}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <ChartState height={200}><LogoLoading size={40} /></ChartState>
        ) : chartData.length === 0 ? (
          <ChartState height={200}>{t('no_data')}</ChartState>
        ) : (
          <ChartFrame height={200}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradCash" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.cash} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={C.cash} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={60} tickFormatter={Y_FMT} />
              <Tooltip {...TOOLTIP_STYLE} formatter={(v, n) => [fmt(Number(v ?? 0), currency), String(n)]} />
              <Area type="monotone" dataKey="cash" name={t('profit')} stroke={C.cash} strokeWidth={2} fill="url(#gradCash)" />
            </AreaChart>
          </ChartFrame>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Revenue vs Expenses Area Chart ───────────────────────────────────────────

function RevenueChart({ currency, pp }: { currency: string; pp: PeriodProps }) {
  const { t } = useI18n();
  const enabled = pp.period !== 'custom' || (!!pp.customFrom && !!pp.customTo);
  const q = useQuery<{ points: ChartPoint[] }, ApiError>({
    queryKey: ['dashboard', 'chart-area', pp.period, pp.customFrom, pp.customTo],
    queryFn: () => api<{ points: ChartPoint[] }>(`/dashboard/chart?${buildQueryParams(pp)}`),
    enabled,
  });
  const chartData = (q.data?.points ?? []).map((p) => ({
    name: formatChartLabel(p.label, pp.period),
    revenue: Number(p.revenue),
    expenses: Number(p.expenses),
    profit: Number(p.profit),
  }));

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle className="text-sm font-medium">{t('revenue_vs_expenses')}</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <ChartState height={200}><LogoLoading size={40} /></ChartState>
        ) : chartData.length === 0 ? (
          <ChartState height={200}>{t('no_data')}</ChartState>
        ) : (
          <ChartFrame height={220}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradR" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.revenue} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={C.revenue} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradE" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.expenses} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={C.expenses} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradP" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.profit} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={C.profit} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={60} tickFormatter={Y_FMT} />
              <Tooltip {...TOOLTIP_STYLE} formatter={(v, n) => [fmt(Number(v ?? 0), currency), String(n)]} />
                            <Area type="monotone" dataKey="expenses" name={t('expenses')} stroke={C.expenses} strokeWidth={2} fill="url(#gradE)" />
              <Area type="monotone" dataKey="profit"   name={t('profit')}   stroke={C.profit}   strokeWidth={2} fill="url(#gradP)" />
              <Area type="monotone" dataKey="revenue"  name={t('revenue')}  stroke={C.revenue}  strokeWidth={2} fill="url(#gradR)" />
            </AreaChart>
          </ChartFrame>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Feed list ────────────────────────────────────────────────────────────────

function FeedCard({ title, empty, children }: { title: string; empty: string; children?: ReactNode }) {
  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {children ?? <p className="py-4 text-sm text-muted-foreground">{empty}</p>}
      </CardContent>
    </Card>
  );
}

/**
 * A band heading. Four chart cards in a row with no labelling reads as a wall;
 * an eyebrow plus a rule tells you the page is made of sections, and gives the
 * period switch somewhere to live other than floating in the gutter.
 */
function Band({ title, aside }: { title: string; aside?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
      <h2 className="eyebrow">{title}</h2>
      <div className="h-px min-w-8 flex-1 bg-border/70" aria-hidden />
      {aside}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();

  // ── Single shared period state ──────────────────────────────────────────────
  const [period, setPeriod] = useState<ChartPeriod>('year');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const pp: PeriodProps = { period, customFrom, customTo };

  const summaryQuery = useQuery<DashboardSummary, ApiError>({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => api<DashboardSummary>('/dashboard/summary'),
  });

  const greetName = user?.name?.split(' ')[0] ?? 'there';

  const head = (
    <PageHead
      title={`${t('welcome')}, ${greetName}`}
      subtitle={
        lang === 'ar'
          ? 'لمحة عن أداء جوين فيوجر لهذا الشهر، التجديدات القادمة، والنشاط الأخير.'
          : 'A snapshot of Join Future this month — upcoming renewals and recent activity.'
      }
    />
  );

  if (summaryQuery.isLoading) {
    return (
      <>
        {head}
        <div className="flex min-h-60 items-center justify-center">
          <LogoLoading size={72} label={t('loading')} />
        </div>
      </>
    );
  }

  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <>
        {head}
        <EmptyState
          title={t('no_data')}
          sub={t('dashboard_unavailable')}
          action={
            <Button variant="outline" size="sm" onClick={() => summaryQuery.refetch()}>
              {lang === 'ar' ? 'إعادة المحاولة' : 'Retry'}
            </Button>
          }
        />
      </>
    );
  }

  const data = summaryQuery.data;
  const { stats, recent_activity, upcoming } = data;
  const currency = data.currency ?? 'BHD';

  return (
    /*
     * Rhythm: 24px between bands, 16px inside one. A dashboard where every gap
     * is the same size has no structure — the reader has to work out which
     * cards belong together. Two gap sizes and a labelled rule do that for them.
     */
    <div className="space-y-6">
      {head}

      {/* ── The headline figures ────────────────────────────────────────
          One panel, four columns. See components/ui/StatBand for why this is
          not four cards, and why each figure carries a comparison. */}
      <StatBand
        items={[
          {
            key: 'revenue',
            label: t('revenue_mtd'),
            value: fmt(Number(stats.revenue_mtd ?? 0), currency),
            current: Number(stats.revenue_mtd ?? 0),
            previous: num(stats.revenue_prev),
            formatDelta: (n) => fmt(n, currency),
          },
          {
            key: 'profit',
            label: t('profit_mtd'),
            value: fmt(Number(stats.profit_mtd ?? 0), currency),
            current: Number(stats.profit_mtd ?? 0),
            previous: num(stats.profit_prev),
            formatDelta: (n) => fmt(n, currency),
          },
          {
            key: 'burn',
            label: t('monthly_burn'),
            value: fmt(Number(stats.monthly_burn ?? 0), currency),
            /* Burn is a forward-looking commitment (recurring costs), not a
               month-to-date total — so it is compared against what was actually
               spent in the same window last month, and up is bad. */
            current: Number(stats.monthly_burn ?? 0),
            previous: num(stats.expenses_prev),
            formatDelta: (n) => fmt(n, currency),
            invert: true,
            hint: t('recurring_commitments'),
          },
          {
            key: 'cash',
            label: t('cash_on_hand'),
            value: fmt(Number(stats.cash_on_hand ?? 0), currency),
            hint: t('across_accounts'),
          },
        ]}
      />

      <Band title={t('outstanding')} />
      <ReceivablesPayablesRow stats={stats} currency={currency} />

      <Band
        title={t('performance')}
        aside={
          <PeriodBar
            period={period}
            setPeriod={setPeriod}
            customFrom={customFrom}
            customTo={customTo}
            setCustomFrom={setCustomFrom}
            setCustomTo={setCustomTo}
          />
        }
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <IncomeExpenseChart currency={currency} pp={pp} />
        <TopExpensesChart currency={currency} pp={pp} />
      </div>

      <div className="grid gap-4">
        <CashFlowChart currency={currency} pp={pp} />
        <RevenueChart currency={currency} pp={pp} />
      </div>

      {/* ── Activity & Alerts ────────────────────────────────────────────── */}
      <Band title={t('activity')} />
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <FeedCard title={t('recent_activity')} empty={t('no_recent_activity')}>
          {recent_activity && recent_activity.length > 0 ? (
            /* Capped and scrolled. An audit log is not the point of the page,
               and 20 rows of it pushed everything else off the fold. */
            <ul className="-my-1 max-h-[22rem] divide-y divide-border/60 overflow-y-auto">
              {recent_activity.slice(0, 20).map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-3 py-2.5">
                  <p className="min-w-0 flex-1 text-[0.8125rem]">
                    <strong className="font-medium">{a.actor_name ?? '—'}</strong>{' '}
                    <span className="text-muted-foreground">{a.action}</span> <span>{a.entity}</span>
                  </p>
                  <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                    {formatRelative(a.created_at, lang)}
                  </span>
                </li>
              ))}
            </ul>
          ) : undefined}
        </FeedCard>

        <div className="grid content-start gap-4">
          <FeedCard title={t('overdue_invoices')} empty={t('no_overdue_invoices')}>
            {upcoming?.overdue_invoices && upcoming.overdue_invoices.length > 0 ? (
              <ul className="divide-y">
                {(upcoming.overdue_invoices as unknown as OverdueInvoiceRow[]).map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/invoices/${inv.id}`}
                        className="text-sm font-medium text-brand-ink tabular-nums hover:underline"
                      >
                        {inv.number}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {inv.customer_name ?? '—'} ·{' '}
                        {lang === 'ar' ? `متأخرة ${inv.days_overdue} يوم` : `${inv.days_overdue} days overdue`}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm tabular-nums">{fmt(Number(inv.balance_due), currency)}</span>
                  </li>
                ))}
              </ul>
            ) : undefined}
          </FeedCard>

          <FeedCard title={t('subs_due_7d')} empty={t('no_upcoming_subs')}>
            {upcoming?.subscriptions_due_in_7_days && upcoming.subscriptions_due_in_7_days.length > 0 ? (
              <ul className="divide-y">
                {upcoming.subscriptions_due_in_7_days.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{s.service}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.vendor_name ?? '—'} · {t('next_charge')}: {formatDate(s.next_charge_date, lang)}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm tabular-nums">{fmt(Number(s.cost), s.currency)}</span>
                  </li>
                ))}
              </ul>
            ) : undefined}
          </FeedCard>
        </div>
      </div>
    </div>
  );
}
